/* eslint-disable @typescript-eslint/naming-convention */
import React from "react";

// interfaces
import { ITransformedGroupedItems } from "@dineout-cart-client/components/steal-store-renderer/types";
import {
    IDineoutCartResponse,
    IDineoutCartResponseData,
    IGeneralRedemptionSectionMeta,
    IGroupedItems,
    IResponseCartItem,
    RenderingDetails,
    RenderingDetailsMeta,
    RenderingDetailsSubDetails,
} from "@swiggy-private/checkout-types";
import { ILocalCart, ILocalCartItem } from "@dineout-cart-client/interfaces/LocalCart";
import { CartMetaData } from "@dineout-cart-client/interfaces/dineoutCartTypes";

// components
import { ItemSublabel } from "@swiggy-private/dineout-cart-ui";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import { REDEMPTION_META_SECTION_KEYS } from "@dineout-cart-client/constants/TextDict";

interface IRenderingDetails {
    type: string;
    key: string;
    displayText?: string;
    value?: string;
    sub_details?: RenderingDetailsSubDetails[];
    meta?: RenderingDetailsMeta;
    is_negative?: number;
}

export const getRenderingDetails = ({
    type,
    key,
    displayText,
    value,
    sub_details = [],
    meta = {},
    is_negative = 0,
}: IRenderingDetails): RenderingDetails => {
    return {
        type,
        key,
        display_text: displayText || "",
        intermediateText: "",
        value: value || "",
        hierarchy: 1,
        currency: "rupees",
        info_text: "",
        icon: "",
        discount_message: "",
        is_negative,
        meta,
        sub_details,
    };
};

export const getRenderingSubDetails = ({ type, key, displayText }: IRenderingDetails): RenderingDetailsSubDetails => {
    return {
        type,
        key,
        display_text: displayText || "",
        value: "",
        hierarchy: 1,
        currency: "rupees",
        info_text: "",
        intermediateText: "",
        icon: "",
        discount_message: "",
        is_collapsible: 0,
        is_negative: 0,
    };
};

export const getRenderingSeprator = () => {
    return {
        type: "separator",
        key: "line_separator",
        display_text: "Line Separator",
        intermediateText: "",
        value: "",
        hierarchy: 1,
        currency: "rupees",
        info_text: "",
        icon: "",
        discount_message: "",
    };
};

export const getGroupCartRendererItems = (
    cartResponse?: IDineoutCartResponseData | null,
): { groupCartItems: ITransformedGroupedItems[]; totalItems: number; items: IResponseCartItem[] } => {
    if (isEmpty(cartResponse?.groupedItems) || isEmpty(cartResponse?.brandInfo)) {
        return { groupCartItems: [], totalItems: 0, items: [] };
    }

    const { groupedItems = [], brandInfo = [] } = cartResponse ?? {};
    let totalItems = 0;
    const items: IResponseCartItem[] = [];

    // Create a map for quick brand lookup
    const brandMap = new Map(brandInfo.map((brand) => [brand.id, brand]));

    // Filter and map grouped items with brand information
    const groupCartItems = groupedItems
        .filter((group) => brandMap.has(group.brandId || ""))
        .map((group) => {
            const brand = brandMap.get(group.brandId || "");

            totalItems += group.itemTotalCount || 0;

            return {
                ...group,
                brandId: brand?.id || "",
                brandName: brand?.name || "",
                brandLogo: brand?.logo || "",
            };
        });

    groupedItems.forEach((group) => {
        if (group.items) {
            group.items.forEach((item) => items.push(item));
        }
    });

    return { groupCartItems, totalItems, items };
};

/** Same pattern as CartItemListWidget: updatingItem = item currently being updated, isUpdateLoading = updateCartInProgress */
interface IGetCartItemListOptions {
    updatingItem?: ILocalCartItem | null;
}

export const getCartItemList = (
    groupCart: ITransformedGroupedItems["items"],
    options?: IGetCartItemListOptions,
    brandName?: string,
) => {
    const { updatingItem = null } = options ?? {};
    const itemList = groupCart.map((item) => {
        const itemId = item?.itemId || "";
        const updating = updatingItem?.itemId === itemId;
        return {
            itemName: item?.ticketAttributes?.title || "",
            outOfStock: item?.outOfStock || false,
            itemPrice: item?.price || 0,
            quantity: item?.quantity || 0,
            itemId,
            vegType: item?.ticketAttributes?.isVeg,
            itemSubtext: React.createElement(ItemSublabel, {
                text: item?.validitySubtext,
                isItemOutOfStock: item?.outOfStock,
            }),
            totalBasePrice: item?.ticketAttributes?.totalBasePrice || 0,
            min: 0,
            max: 0,
            validityDuration: item?.validityDuration,
            validitySubtext: item.validitySubtext,
            validOnAllOutlets: item?.validOnAllOutlets,
            eligibleOutlets: item?.eligibleOutlets,
            ineligibleOutlets: item?.ineligibleOutlets,
            isUpdating: updating,
            brandName,
            tncs: item.tncs,
        };
    });

    return itemList;
};

export const getRedemptionMeta = (cartResponse?: IDineoutCartResponseData | null) => {
    return cartResponse?.generalRedemptionSectionMeta;
};

export const processRedemptionMeta = (redemptionMeta?: IGeneralRedemptionSectionMeta, item?: IResponseCartItem) => {
    if (!redemptionMeta) {
        return null;
    }

    const sections = redemptionMeta?.sections?.map((section) => {
        const points = section.points ?? [];
        if (section.key === REDEMPTION_META_SECTION_KEYS.TNC && !isEmpty(item?.tncs)) {
            return { ...section, points: [...points, ...(item?.tncs ?? [])] };
        }
        return { ...section, points };
    });

    return {
        ...redemptionMeta,
        sections,
    };
};

interface IStealDealCartItemsByAvailability {
    availableItems: ILocalCartItem[];
    unavailableItems: ILocalCartItem[];
}

export const getStealDealAvailableCartItems = (
    groupedItems?: IGroupedItems[] | null,
): IStealDealCartItemsByAvailability => {
    if (!groupedItems) {
        return { availableItems: [], unavailableItems: [] };
    }

    const availableItems: ILocalCartItem[] = [];
    const unavailableItems: ILocalCartItem[] = [];

    groupedItems.forEach((group) => {
        group.items.forEach((item) => {
            const cartItem = item as ILocalCartItem;
            if (item.outOfStock) {
                unavailableItems.push(cartItem);
            } else {
                availableItems.push(cartItem);
            }
        });
    });

    return { availableItems, unavailableItems };
};

export const prepareStealDealLocalCartFromReviewedCart = (
    flowType: string,
    currentLocalCart: ILocalCart,
    apiResponse: IDineoutCartResponse,
): ILocalCart => {
    const { adjustToAvailableQuantity: _transient, ...sanitizedCartMetaData } = currentLocalCart?.cartMetaData ?? {};
    return {
        ...currentLocalCart,
        flowType,
        items: currentLocalCart?.items,
        latestCartCallTimestamp: Date.now(),
        latestCartResponse: apiResponse,
        cartMetaData: sanitizedCartMetaData as CartMetaData,
    };
};

/**
 * Reconciles local cart item quantities with BE response quantities.
 * Always syncs with BE regardless of status code, as BE is the source of truth.
 */
export const reconcileLocalCartItemsWithBE = (params: ILocalCart, cartResponse: IDineoutCartResponse): ILocalCart => {
    const { items: reviewedCartItems } = getGroupCartRendererItems(cartResponse.data);
    const updatedItems = params.items
        ? params.items
              .map((cartItem): ILocalCartItem | null => {
                  const reviewedCartItem = reviewedCartItems.find(
                      (item) => String(item.itemId) === String(cartItem.itemId),
                  );
                  return reviewedCartItem ? { ...cartItem, quantity: reviewedCartItem.quantity } : null;
              })
              .filter((item): item is ILocalCartItem => item != null)
        : undefined;

    const { adjustToAvailableQuantity: _transient, ...sanitizedCartMetaData } = params?.cartMetaData ?? {};
    return { ...params, items: updatedItems, cartMetaData: sanitizedCartMetaData as CartMetaData };
};
